import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getSeasonStats, 
  updateSeasonPlayerStats, 
  updatePlayerCareerStats,
  calculateBattingStats, 
  getPlayer,
  getGames,
  getSeasonGames 
} from '../firebase/firestore';
import {
  ensureNumber,
  calculateTotalBases,
  calculateBattingAverage,
  calculateOBP,
  calculateSLG,
  calculateOPS,
  calculateCompleteBattingStats
} from '../utils/calculations';

// Default empty stats objects to use when no stats are available
const EMPTY_BATTING_STATS = {
  games: 0,
  atBats: 0,
  hits: 0,
  doubles: 0,
  triples: 0,
  homeRuns: 0,
  runs: 0,
  walks: 0,
  hitByPitch: 0,
  strikeouts: 0,
  rbi: 0,
  average: 0,
  obp: 0,
  slg: 0,
  ops: 0,
  totalBases: 0,
  sacrificeFlies: 0
};

const EMPTY_PITCHING_STATS = {
  games: 0,
  inningsPitched: 0,
  strikeouts: 0,
  walks: 0,
  hits: 0,
  earnedRuns: 0,
  era: 0,
  whip: 0,
  wins: 0,
  losses: 0
};

/**
 * Calculate derived batting statistics from raw values
 * @param {object} rawStats - The raw batting statistics
 * @returns {object} - The raw stats plus calculated values like average, OBP, SLG, etc.
 */
const calculateDerivedStats = (rawStats) => {
  if (!rawStats) return { ...EMPTY_BATTING_STATS };
  
  // Use the utility function to calculate complete stats
  const calculatedStats = calculateCompleteBattingStats(rawStats);
  
  // Ensure all required fields exist
  return {
    ...EMPTY_BATTING_STATS,
    ...calculatedStats,
    rbi: ensureNumber(calculatedStats.rbi)
  };
};

/**
 * Validates stats to ensure they're reasonable and internally consistent
 * @param {object} stats - The stats object to validate
 * @returns {object} - Validation result with isValid flag and issues array
 */
const validateStats = (stats) => {
  if (!stats) return { isValid: false, issues: ['Stats object is null'] };

  const issues = [];
  
  // Check for required fields
  const requiredFields = ['atBats', 'hits', 'games'];
  requiredFields.forEach(field => {
    if (stats[field] === undefined || stats[field] === null) {
      issues.push(`Missing required field: ${field}`);
    }
  });

  // Validate relationships between stats
  if (ensureNumber(stats.hits) > ensureNumber(stats.atBats)) {
    issues.push(`Hits (${stats.hits}) cannot be greater than At Bats (${stats.atBats})`);
  }

  const extraBaseHits = ensureNumber(stats.doubles) + ensureNumber(stats.triples) + ensureNumber(stats.homeRuns);
  if (extraBaseHits > ensureNumber(stats.hits)) {
    issues.push(`Extra base hits (${extraBaseHits}) cannot exceed total hits (${stats.hits})`);
  }
  
  // Validate derived stats if present
  if (ensureNumber(stats.atBats) > 0) {
    const expectedAvg = calculateBattingAverage(stats.hits, stats.atBats);
    if (stats.average !== undefined && Math.abs(stats.average - expectedAvg) > 0.001) {
      issues.push(`Batting average inconsistency: stored ${stats.average.toFixed(3)} vs calculated ${expectedAvg.toFixed(3)}`);
    }
    
    // Calculate expected total bases
    const expectedTotalBases = calculateTotalBases(
      stats.hits,
      stats.doubles,
      stats.triples,
      stats.homeRuns
    );
                             
    if (stats.totalBases !== undefined && Math.abs(stats.totalBases - expectedTotalBases) > 0.001) {
      issues.push(`Total bases inconsistency: stored ${stats.totalBases} vs calculated ${expectedTotalBases}`);
    }
  }
  
  return { isValid: issues.length === 0, issues };
};

/**
 * Custom hook to manage player stats with caching and validation
 * @param {string} playerId - ID of the player to get stats for
 * @param {object} activeSeason - The active season object
 * @param {boolean} viewingAllSeasons - Whether we're viewing career stats or season stats
 * @param {number} dataVersion - Version number from PlayerContext to detect data changes
 * @param {object} playerData - Player data from PlayerContext to avoid duplicate fetches
 * @returns {object} - Stats data and related functions
 */
export const useStats = (playerId, activeSeason, viewingAllSeasons, dataVersion, playerData = null) => {
  const { currentUser } = useAuth();
  const [battingStats, setBattingStats] = useState(EMPTY_BATTING_STATS);
  const [pitchingStats, setPitchingStats] = useState(EMPTY_PITCHING_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('loading');
  const [debugMode, setDebugMode] = useState(true); // Set debug mode on by default for troubleshooting

  // Cache stats to avoid redundant calculations
  const statsCache = useRef({
    career: null,
    seasons: {}
  });

  // Track last seen dataVersion to detect when data has changed in PlayerContext
  const lastDataVersion = useRef(dataVersion);
  
  
  // Context for tracking current view
  const [statsContext, setStatsContext] = useState({
    viewType: viewingAllSeasons ? 'career' : 'season',
    seasonId: activeSeason?.id,
    lastUpdated: new Date().toISOString()
  });
  
  // Store previous context for comparison
  const prevContextRef = useRef({
    viewType: viewingAllSeasons ? 'career' : 'season',
    seasonId: activeSeason?.id
  });
  // Validation results
  const validationResults = useRef({ isValid: true, issues: [] });
  
  // Debug info for diagnostics
  const [diagnosticInfo, setDiagnosticInfo] = useState({});

  /**
   * Fetches the appropriate stats based on the current context (season or career)
   * @param {boolean} forceRefresh - Whether to force a refresh from the database
   * @returns {Promise<void>}
   */
  const fetchStats = useCallback(async (forceRefresh = false) => {
    console.log('🔄 STATS DEBUG: fetchStats called with params:', {
      playerId,
      seasonId: activeSeason?.id,
      viewingAllSeasons,
      forceRefresh,
      cacheState: {
        hasCareerCache: !!statsCache.current.career,
        hasSeasonCache: activeSeason?.id ? !!statsCache.current.seasons[activeSeason.id] : false
      }
    });
    
    // Early exit if missing required data
    if (!currentUser || !playerId) {
      console.warn('❌ STATS DEBUG: Missing user or player data', { currentUser: !!currentUser, playerId });
      setLoading(false);
      setError('No user or player selected');
      setDiagnosticInfo({ error: 'Missing user or player' });
      return;
    }
    
    // Log data flow state
    console.log('🌊 STATS DEBUG: Current data flow state:', {
      playerId,
      seasonId: activeSeason?.id,
      viewingAllSeasons,
      battingStatsState: {
        games: battingStats.games,
        atBats: battingStats.atBats,
        hits: battingStats.hits
      }
    });

    try {
      setLoading(true);
      setError(null);
      
      // Log context for debugging
      console.log(`📊 STATS DEBUG: Fetching stats for player ${playerId}:`, {
        viewType: viewingAllSeasons ? 'career' : 'season',
        seasonId: activeSeason?.id,
        forceRefresh,
        currentBattingStats: {
          games: battingStats.games,
          atBats: battingStats.atBats,
          hits: battingStats.hits,
          average: battingStats.average
        }
      });
      
      // Check if context has changed
      const contextChanged = prevContextRef.current.viewType !== (viewingAllSeasons ? 'career' : 'season') || 
                           prevContextRef.current.seasonId !== activeSeason?.id;
      
      if (contextChanged) {
        console.log('🔄 STATS DEBUG: Context changed:', {
          from: prevContextRef.current,
          to: { viewType: viewingAllSeasons ? 'career' : 'season', seasonId: activeSeason?.id }
        });
      }
      
      // Update previous context reference
      prevContextRef.current = {
        viewType: viewingAllSeasons ? 'career' : 'season',
        seasonId: activeSeason?.id
      };
      
      // Update context
      const newContext = {
        viewType: viewingAllSeasons ? 'career' : 'season',
        seasonId: activeSeason?.id,
        lastUpdated: new Date().toISOString()
      };
      
      console.log('📝 STATS DEBUG: Setting new stats context:', newContext);
      setStatsContext(newContext);

      let dataSourceType = 'unknown';

      // CAREER STATS HANDLING
      if (viewingAllSeasons) {
        // Check cache first if not forcing refresh
        if (!forceRefresh && statsCache.current.career) {
          console.log('Using cached career stats');
          setBattingStats(statsCache.current.career.batting);
          setPitchingStats(statsCache.current.career.pitching || {});
          dataSourceType = 'cache_career';
          
          setDiagnosticInfo({
            source: 'cached_career_stats',
            games: statsCache.current.career.batting.games || 0,
            atBats: statsCache.current.career.batting.atBats || 0,
            hits: statsCache.current.career.batting.hits || 0
          });
        } else {
          // Use playerData from PlayerContext to avoid duplicate fetch
          console.log('Using career stats from PlayerContext');

          if (playerData) {
            // If forcing refresh, recalculate from all games
            if (forceRefresh) {
              console.log('Force refreshing career stats from all games');
              const allGames = await getGames(currentUser.uid, playerId);
              
              console.log(`🎮 STATS DEBUG: Retrieved ${allGames?.length || 0} games for career calculation:`, {
                gamesWithBattingStats: allGames?.filter(g => g.battingStats)?.length || 0,
                recentGameDates: allGames?.slice(-3).map(g => ({ id: g.id, date: g.date })) || []
              });
              
              // Log game stats totals before calculation
              if (allGames && allGames.length > 0) {
                const totalAtBats = allGames.reduce((sum, game) => sum + Number(game.battingStats?.atBats || 0), 0);
                const totalHits = allGames.reduce((sum, game) => sum + Number(game.battingStats?.hits || 0), 0);
                console.log('🧮 STATS DEBUG: Game data pre-calculation totals:', {
                  totalGames: allGames.length,
                  totalAtBats,
                  totalHits,
                  expectedAverage: totalAtBats > 0 ? (totalHits / totalAtBats).toFixed(3) : '0.000'
                });
              }
              
              if (allGames && allGames.length > 0) {
                console.log(`📊 STATS DEBUG: Recalculating career stats from ${allGames.length} games`);
                const calculatedBattingStats = calculateBattingStats(allGames);
                
                // Log the calculated results
                console.log('📈 STATS DEBUG: Calculated career batting stats:', {
                  games: calculatedBattingStats.games,
                  atBats: calculatedBattingStats.atBats,
                  hits: calculatedBattingStats.hits,
                  average: calculatedBattingStats.average,
                  homeRuns: calculatedBattingStats.homeRuns,
                  rbi: calculatedBattingStats.rbi
                });
                
                // Store recalculated stats back to player document
                console.log('💾 STATS DEBUG: Updating player career stats in database');
                await updatePlayerCareerStats(currentUser.uid, playerId, {
                  battingStats: calculatedBattingStats
                });
                
                // Process and use the recalculated stats
                const processedStats = calculateDerivedStats(calculatedBattingStats);
                console.log('✅ STATS DEBUG: Processed career stats with derived values:', {
                  games: processedStats.games,
                  atBats: processedStats.atBats,
                  hits: processedStats.hits,
                  average: processedStats.average,
                  obp: processedStats.obp,
                  slg: processedStats.slg,
                  ops: processedStats.ops
                });
                
                setBattingStats(processedStats);
                dataSourceType = 'recalculated_career';
                
                // Update cache
                statsCache.current.career = {
                  batting: processedStats,
                  pitching: playerData.pitchingStats || {}
                };
                
                setDiagnosticInfo({
                  source: 'recalculated_career_stats',
                  gamesCount: allGames.length,
                  atBats: calculatedBattingStats.atBats || 0,
                  hits: calculatedBattingStats.hits || 0,
                  average: calculatedBattingStats.average || 0
                });
              } else {
                console.log('No games found for career stats');
                setBattingStats(EMPTY_BATTING_STATS);
                dataSourceType = 'empty_career';
              }
            } else {
              // Use pre-calculated stats from player document
              const processedStats = calculateDerivedStats(playerData.battingStats || {});
              setBattingStats(processedStats);
              setPitchingStats(playerData.pitchingStats || {});
              dataSourceType = 'stored_career';
              
              // Update cache
              statsCache.current.career = {
                batting: processedStats,
                pitching: playerData.pitchingStats || {}
              };
              
              setDiagnosticInfo({
                source: 'stored_career_stats',
                games: processedStats.games || 0,
                atBats: processedStats.atBats || 0,
                hits: processedStats.hits || 0
              });
            }
            
            // Validate the career stats for inconsistencies
            validationResults.current = validateStats(battingStats);
            if (!validationResults.current.isValid && debugMode) {
              console.warn('Career stats validation issues:', validationResults.current.issues);
            }
          } else {
            console.error('Player data not found');
            setError('Player data not found');
            dataSourceType = 'error_career';
          }
        }
      }
      // SEASON STATS HANDLING
      // SEASON STATS HANDLING
      else if (activeSeason?.id) {
        const seasonId = activeSeason.id;
        
        console.log(`🏆 STATS DEBUG: Processing season stats for season ${seasonId}`);
        
        // Check cache first if not forcing refresh
        if (!forceRefresh && statsCache.current.seasons[seasonId]) {
          setBattingStats(statsCache.current.seasons[seasonId].batting);
          setPitchingStats(statsCache.current.seasons[seasonId].pitching || {});
          dataSourceType = 'cache_season';
          
          setDiagnosticInfo({
            source: 'cached_season_stats',
            seasonId,
            games: statsCache.current.seasons[seasonId].batting.games || 0,
            atBats: statsCache.current.seasons[seasonId].batting.atBats || 0,
            hits: statsCache.current.seasons[seasonId].batting.hits || 0
          });
        } else {
          // If explicitly forcing refresh (from original request)
          if (forceRefresh) {
            console.log(`🔄 STATS DEBUG: Force refreshing stats for season ${seasonId}`);
            console.log('💾 STATS DEBUG: Calling updateSeasonPlayerStats to recalculate in database');
            await updateSeasonPlayerStats(currentUser.uid, playerId, seasonId);
            console.log('✅ STATS DEBUG: Season stats updated in database');
          }
          
          // Get stats from Firebase
          console.log(`📥 STATS DEBUG: Fetching stats for season ${seasonId} from database`);
          const seasonStatsData = await getSeasonStats(currentUser.uid, playerId, seasonId);
          console.log('📊 STATS DEBUG: Retrieved season stats data:', seasonStatsData ? {
            games: seasonStatsData.battingStats?.games || 0,
            atBats: seasonStatsData.battingStats?.atBats || 0,
            hits: seasonStatsData.battingStats?.hits || 0,
            average: seasonStatsData.battingStats?.average || 0
          } : 'No stats data found');
          
          if (seasonStatsData) {
            // Process the stats 
            console.log('🧮 STATS DEBUG: Processing season stats from database results');
            const processedStats = calculateDerivedStats(seasonStatsData.battingStats || {});
            console.log('✅ STATS DEBUG: Processed season stats result:', {
              games: processedStats.games,
              atBats: processedStats.atBats,
              hits: processedStats.hits,
              average: processedStats.average,
              obp: processedStats.obp,
              slg: processedStats.slg,
              ops: processedStats.ops
            });
            
            setBattingStats(processedStats);
            setPitchingStats(seasonStatsData.pitchingStats || {});
            dataSourceType = 'stored_season';
            
            // Update cache
            statsCache.current.seasons[seasonId] = {
              batting: processedStats,
              pitching: seasonStatsData.pitchingStats || {}
            };
            
            setDiagnosticInfo({
              source: 'stored_season_stats',
              seasonId,
              games: processedStats.games || 0,
              atBats: processedStats.atBats || 0,
              hits: processedStats.hits || 0,
              calculated: {
                average: processedStats.average
              }
            });
            
            // Validate the season stats for consistency
            validationResults.current = validateStats(processedStats);
            if (!validationResults.current.isValid && debugMode) {
              console.warn(`Season stats validation failed for season ${seasonId}:`, 
                validationResults.current.issues);
            }
          } else {
            console.log(`No stats found for season ${seasonId}, creating empty stats`);
            setBattingStats(EMPTY_BATTING_STATS);
            setPitchingStats(EMPTY_PITCHING_STATS);
            dataSourceType = 'empty_season';
            
            setDiagnosticInfo({
              source: 'empty_season_stats',
              seasonId,
              error: 'No stats found for this season'
            });
          }
        }
      } else {
        // No active season and not viewing all seasons
        console.log('No active season selected');
        setError('No active season selected');
        setBattingStats(EMPTY_BATTING_STATS);
        setPitchingStats(EMPTY_PITCHING_STATS);
        dataSourceType = 'no_season';
        
        setDiagnosticInfo({
          source: 'no_season_selected',
          error: 'No active season and not viewing all seasons'
        });
      }
      
      // Set the data source for tracking
      setDataSource(dataSourceType);
      
    } catch (err) {
      console.error('Error fetching stats:', err);
      setBattingStats(EMPTY_BATTING_STATS);
      setPitchingStats(EMPTY_PITCHING_STATS);
      setDataSource('error');
      
      setDiagnosticInfo({
        source: 'error',
        error: err.message,
        stack: err.stack
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, playerId, activeSeason, viewingAllSeasons, debugMode]);

  /**
   * Forces a refresh of the stats from the server
   * @returns {Promise<void>}
   */
  /**
   * Forces a refresh of the stats from the server
   * @returns {Promise<boolean>} - True if refresh was successful, false otherwise
   */
  const refreshStats = useCallback(async () => {
    console.log('🔄 STATS DEBUG: Manual stats refresh requested', {
      playerId,
      seasonId: activeSeason?.id,
      viewType: viewingAllSeasons ? 'career' : 'season',
      currentStats: {
        games: battingStats.games,
        atBats: battingStats.atBats,
        hits: battingStats.hits,
        average: battingStats.average,
        dataSource: dataSource,
        cacheStatus: {
          hasCareerCache: !!statsCache.current.career,
          hasSeasonCache: activeSeason?.id ? !!statsCache.current.seasons[activeSeason.id] : false
        }
      }
    });
    
    // Check if we should throttle force refreshes
    const now = Date.now();
    const timeSinceLastForceRefresh = now - lastForceRefreshRef.current;
    
    if (timeSinceLastForceRefresh < forceRefreshTimeMs) {
      console.log(`🛑 STATS DEBUG: Throttling force refresh (${timeSinceLastForceRefresh}ms since last force refresh, threshold is ${forceRefreshTimeMs}ms)`);
      console.log('📦 STATS DEBUG: Using cached stats instead of forcing refresh');
      
      // Use cache if available
      if ((viewingAllSeasons && statsCache.current.career) || 
          (!viewingAllSeasons && activeSeason?.id && statsCache.current.seasons[activeSeason.id])) {
        
        if (viewingAllSeasons && statsCache.current.career) {
          setBattingStats(statsCache.current.career.batting);
          setPitchingStats(statsCache.current.career.pitching || {});
          setDataSource('throttled_manual_refresh_career');
        } else if (!viewingAllSeasons && activeSeason?.id && statsCache.current.seasons[activeSeason.id]) {
          setBattingStats(statsCache.current.seasons[activeSeason.id].batting);
          setPitchingStats(statsCache.current.seasons[activeSeason.id].pitching || {});
          setDataSource('throttled_manual_refresh_season');
        }
        
        return true;
      }
    }
    
    try {
      // Update last force refresh timestamp
      lastForceRefreshRef.current = now;
      // Clear relevant cache entries
      if (viewingAllSeasons) {
        console.log('🧹 STATS DEBUG: Clearing career stats cache');
        statsCache.current.career = null;
        
        // When refreshing career stats, also clear season caches
        // since they contribute to career totals
        console.log('🧹 STATS DEBUG: Also clearing all season caches');
        statsCache.current.seasons = {};
      } else if (activeSeason?.id) {
        console.log(`🧹 STATS DEBUG: Clearing cache for season ${activeSeason.id}`);
        statsCache.current.seasons[activeSeason.id] = null;
        
        // When refreshing season stats, also clear career cache
        // since it includes this season's stats
        console.log('🧹 STATS DEBUG: Also clearing career cache');
        statsCache.current.career = null;
      }
      
      console.log('🔄 STATS DEBUG: Forcing complete stats recalculation from database');
      
      // Call fetchStats with forceRefresh = true
      await fetchStats(true);
      
      console.log('✅ STATS DEBUG: Stats refresh completed', {
        newStats: {
          games: battingStats.games,
          atBats: battingStats.atBats,
          hits: battingStats.hits,
          average: battingStats.average,
          dataSource: dataSource
        }
      });
      // Verify stats after refresh are valid
      const statsValidation = validateStats(battingStats);
      if (!statsValidation.isValid) {
        console.warn('⚠️ STATS DEBUG: Refreshed stats validation failed:', statsValidation.issues);
      } else {
        console.log('✅ STATS DEBUG: Refreshed stats passed validation');
      }
      
      return true;
    } catch (error) {
      console.error('❌ STATS DEBUG: Error during stats refresh:', error);
      return false;
    }
  }, [fetchStats, playerId, activeSeason, viewingAllSeasons]);
  
  /**
   * Toggles debug mode for additional logging and diagnostics
   */
  const toggleDebugMode = useCallback(() => {
    setDebugMode(prev => {
      const newMode = !prev;
      console.log(`🐞 STATS DEBUG: Debug mode ${newMode ? 'enabled' : 'disabled'}`);
      
      // Update diagnostic info
      setDiagnosticInfo(prev => ({
        ...prev,
        debugMode: newMode,
        toggledAt: new Date().toISOString()
      }));
      
      return newMode;
    });
  }, []);

  /**
   * Clears the stats cache to force fresh data on next fetch
   */
  const clearCache = useCallback(() => {
    console.log('🧹 STATS DEBUG: Clearing entire stats cache');
    statsCache.current = {
      career: null,
      seasons: {}
    };
    
    // Add to diagnostic info
    setDiagnosticInfo(prev => ({
      ...prev,
      cacheCleared: true,
      clearedAt: new Date().toISOString()
    }));
  }, []);

  // Add throttling mechanism with more aggressive settings
  const lastFetchRef = useRef(0);
  const throttleTimeMs = 30000; // 30 seconds between fetches (much more aggressive)
  const forceRefreshTimeMs = 300000; // Only force refresh every 5 minutes
  const lastForceRefreshRef = useRef(0);
  
  // Fetch stats when dependencies change, with throttling
  useEffect(() => {
    console.log('🔄 STATS DEBUG: Dependencies changed, checking if fetch is needed', {
      playerId,
      seasonId: activeSeason?.id,
      viewingAllSeasons,
      dataVersion
    });

    // Check if dataVersion changed - if so, clear cache
    if (dataVersion !== lastDataVersion.current) {
      console.log(`🧹 STATS DEBUG: DataVersion changed from ${lastDataVersion.current} to ${dataVersion} - clearing cache`);
      statsCache.current = {
        career: null,
        seasons: {}
      };
      lastDataVersion.current = dataVersion;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchRef.current;

    // Check if we should throttle the request
    if (timeSinceLastFetch < throttleTimeMs) {
      console.log(`🛑 STATS DEBUG: Throttling fetch request (${timeSinceLastFetch}ms since last fetch, threshold is ${throttleTimeMs}ms)`);

      // Use cache if available
      if ((viewingAllSeasons && statsCache.current.career) ||
          (!viewingAllSeasons && activeSeason?.id && statsCache.current.seasons[activeSeason.id])) {
        console.log('📦 STATS DEBUG: Using cached stats instead of fetching');

        if (viewingAllSeasons && statsCache.current.career) {
          setBattingStats(statsCache.current.career.batting);
          setPitchingStats(statsCache.current.career.pitching || {});
          setDataSource('throttled_cache_career');
        } else if (!viewingAllSeasons && activeSeason?.id && statsCache.current.seasons[activeSeason.id]) {
          setBattingStats(statsCache.current.seasons[activeSeason.id].batting);
          setPitchingStats(statsCache.current.seasons[activeSeason.id].pitching || {});
          setDataSource('throttled_cache_season');
        }

        return;
      }
    }

    // If we're not throttling or don't have cache, fetch the stats
    console.log('✅ STATS DEBUG: Fetching stats (not throttled or no cache available)');
    fetchStats();
    lastFetchRef.current = now;
  }, [fetchStats, playerId, activeSeason?.id, viewingAllSeasons, dataVersion]);

  // Return the hook data and functions
  return {
    battingStats,
    pitchingStats,
    loading,
    error,
    dataSource,
    statsContext,
    diagnosticInfo,
    validationResults: validationResults.current,
    debugMode,
    // Functions
    refreshStats,
    toggleDebugMode,
    clearCache,
    validateStats
  };
};
